import {
  ActiveOrganizationProvider,
  HttpApiProvider,
  SessionProvider,
} from '@repo/api-client/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { ToastsProvider } from '@/components/ui/use-toast';
import { useActiveOrgStorage } from '@/features/auth/activeOrgStorage';
import { webSessionStorage } from '@/features/auth/sessionStorage';
import { ThemeProvider } from '@/features/theme';
import {
  API_BASE,
  createAxios,
  initSuperTokensWeb,
  resolveAppOrigin,
  webSessionAdapter,
} from '@/lib/api';

/**
 * Boot SuperTokens once at module load. Subsequent imports are
 * idempotent (the function tracks initialised state internally).
 *
 * We do this at module scope (not inside a useEffect) so the SDK is
 * ready before the SessionProvider's bootstrap runs — otherwise the
 * very first `doesSessionExist()` probe may resolve `false` even when
 * a valid cookie is present.
 */
if (typeof window !== 'undefined') {
  initSuperTokensWeb({ apiDomain: resolveAppOrigin() });
}

/**
 * Build the singleton axios instance + react-query client once per
 * page and feed them to all the providers. In tests we go through
 * `src/test/render.tsx` instead, which builds disposable instances
 * with retries off.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Cache for 60 seconds. Mutations explicitly invalidate
            // their own keys, so this just covers the "user clicks
            // back to a list they just left" case where re-fetching
            // every nav would feel laggy.
            staleTime: 60_000,
            // 5 minute GC so caches survive a brief route change.
            gcTime: 5 * 60_000,
            // Retry idempotent reads twice on transient errors. Server
            // errors get bumped to the user via toasts; auth errors
            // are caught by the response interceptor mapping → React
            // Query never sees them as retriable.
            retry: 2,
            refetchOnWindowFocus: false,
          },
          mutations: {
            retry: 0,
          },
        },
      }),
    [],
  );

  const axios = useMemo(() => createAxios({ baseUrl: API_BASE }), []);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <HttpApiProvider client={axios}>
          <SessionProvider storage={webSessionStorage()} auth={webSessionAdapter()}>
            <ActiveOrgGate>
              <ToastsProvider>{children}</ToastsProvider>
            </ActiveOrgGate>
          </SessionProvider>
        </HttpApiProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

/**
 * Defers mounting children until the persisted active-org id has been
 * read off `localStorage`. Without this, the org guard would see
 * `null` for one render and snap the user to `organizations[0]`,
 * wiping their last-selected workspace on every cold load.
 */
function ActiveOrgGate({ children }: { children: ReactNode }) {
  const { loaded, activeOrganizationId, setActiveOrganizationId } = useActiveOrgStorage();

  if (!loaded) return null;

  return (
    <ActiveOrganizationProvider
      value={activeOrganizationId}
      onChange={setActiveOrganizationId}
    >
      {children}
    </ActiveOrganizationProvider>
  );
}
